import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import http from "../../utils/http";

const initialState = {
  isLoading: false,
  isSaving: false,
  features: [],
  currentFeature: null,
};

const baseUrl = process.env.REACT_APP_API_SIDEKICK_BASE_URL;

const normalizeStatus = (item) => {
  if (typeof item?.status === "string") {
    return item.status.toLowerCase() === "active" ? "Active" : "Inactive";
  }
  if (typeof item?.isActive === "boolean") {
    return item.isActive ? "Active" : "Inactive";
  }
  return "Inactive";
};

const normalizeFeature = (item) => ({
  id: item?._id || item?.id || "",
  title: item?.title,
  description:
    item?.description || "",
  isActive:
    typeof item?.isActive === "boolean"
      ? item.isActive
      : normalizeStatus(item) === "Active",
  status: normalizeStatus(item),
  updated:
    item?.updatedAt ||
    item?.updated_on ||
    item?.createdAt ||
    item?.created_on ||
    "",
  raw: item,
});

export const getAllUpcomingFeatures = createAsyncThunk(
  "upcoming-features/getAll",
  async (_payload, thunkAPI) => {
    try {
      const response = await http.get(`${baseUrl}/get-all-upcoming-features`);
      const raw = response?.data;
      return thunkAPI.fulfillWithValue(raw?.data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const addUpcomingFeature = createAsyncThunk(
  "upcoming-features/add",
  async (payload, thunkAPI) => {
    try {
      const body = {
        title: payload.title,
        description: payload.description,
        isActive: Boolean(payload.isActive),
      };
      const response = await http.post(`${baseUrl}/add-upcoming-feature`, body);
      return thunkAPI.fulfillWithValue(response?.data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const updateUpcomingFeature = createAsyncThunk(
  "upcoming-features/update",
  async ({ id, payload }, thunkAPI) => {
    try {
      const body = {
        title: payload.title,
        description: payload.description,
        isActive: Boolean(payload.isActive),
      };
      const response = await http.patch(
        `${baseUrl}/update-upcoming-feature/${id}`,
        body,
      );
      return thunkAPI.fulfillWithValue(response?.data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const deleteUpcomingFeature = createAsyncThunk(
  "upcoming-features/delete",
  async (id, thunkAPI) => {
    try {
      const response = await http.delete(`${baseUrl}/delete-upcoming-feature/${id}`);
      return thunkAPI.fulfillWithValue({ id, response: response?.data });
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

const upcomingFeatureSlice = createSlice({
  name: "upcoming-features",
  initialState,
  reducers: {
    setCurrentFeature: (state, action) => {
      state.currentFeature = action.payload;
    },
    clearCurrentFeature: (state) => {
      state.currentFeature = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllUpcomingFeatures.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllUpcomingFeatures.fulfilled, (state, action) => {
        state.isLoading = false;
        state.features = Array.isArray(action.payload)
          ? action.payload.map(normalizeFeature)
          : [];
      })
      .addCase(getAllUpcomingFeatures.rejected, (state) => {
        state.isLoading = false;
        state.features = [];
      })
      .addCase(addUpcomingFeature.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(addUpcomingFeature.fulfilled, (state) => {
        state.isSaving = false;
      })
      .addCase(addUpcomingFeature.rejected, (state) => {
        state.isSaving = false;
      })
      .addCase(updateUpcomingFeature.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(updateUpcomingFeature.fulfilled, (state) => {
        state.isSaving = false;
      })
      .addCase(updateUpcomingFeature.rejected, (state) => {
        state.isSaving = false;
      })
      .addCase(deleteUpcomingFeature.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(deleteUpcomingFeature.fulfilled, (state, action) => {
        state.isSaving = false;
        state.features = state.features.filter(
          (item) => item.id !== action.payload.id,
        );
      })
      .addCase(deleteUpcomingFeature.rejected, (state) => {
        state.isSaving = false;
      });
  },
});

export const { setCurrentFeature, clearCurrentFeature } = upcomingFeatureSlice.actions;
export default upcomingFeatureSlice.reducer;
